import { Link } from "react-router-dom";
import { Phone, MapPin, Clock } from "lucide-react";
import { motion } from "framer-motion";
import enzosLogo from "@/assets/enzos-logo.png";
import { getHasArticles } from "@/lib/blogPreload";

// Fat footer: sitewide, crawlable internal links to the money pages and key
// categories. Keeps deep commercial pages (service & repair, wash bay design,
// the brand pressure-washer lines) one static link from every page so link
// equity and crawl paths don't depend on the JS mega-menu.
const quickLinks = [
  { label: "Pressure Washer Repair", path: "/services/pressure-washer-service-repair/" },
  { label: "Wash Bay Design", path: "/cleaning-equipment/wash-bay-design/" },
  { label: "Scheduled Maintenance", path: "/services/scheduled-maintenance/" },
  { label: "Free Consultations", path: "/services/free-consultations/" },
  { label: "Fleet Preservation Program", path: "/fleet-asset-protection/" },
  { label: "Fleet Preservation Process", path: "/fleet-preservation-process/" },
  { label: "Fleet Corrosion Protection", path: "/protect-your-fleet-from-corrosion-downtime/" },
  { label: "Drive-Over Undercarriage Wash", path: "/cleaning-equipment/under-carriage-sprayers/the-neutralizer/" },
  { label: "Touchless Drive Thru", path: "/touchless-drive-thru/" },
  { label: "Pressure Washers", path: "/cleaning-equipment/pressure-washers/" },
  { label: "Hotsy Pressure Washers", path: "/cleaning-equipment/pressure-washers/hotsy-pressure-washers/" },
  { label: "Mi-T-M Equipment", path: "/cleaning-equipment/mi-t-m/" },
  { label: "Undercarriage Wash Systems", path: "/cleaning-equipment/under-carriage-sprayers/" },
  { label: "Cleaning Equipment", path: "/cleaning-equipment/" },
  { label: "Detergents", path: "/detergents/" },
  { label: "Disinfecting", path: "/disinfecting/" },
  { label: "Industries We Serve", path: "/industries-we-serve/" },
  { label: "Fleet Wash Guides", path: "/guides/" },
  { label: "Articles", path: "/articles/" }, // shown only once the partner has published (getHasArticles)
  { label: "About Us", path: "/about-us/" },
  { label: "Contact Us", path: "/contact-us/" },
];

// Legal / privacy links on every page. "Your Privacy Choices" holds the
// consent controls and is the opt-out link US state privacy laws expect.
const legalLinks = [
  { label: "Privacy Policy", path: "/privacy-policy/" },
  { label: "Terms & Conditions", path: "/terms-conditions/" },
  { label: "Cookie Notice", path: "/cookie-notice/" },
  { label: "Lead Attribution Notice", path: "/lead-attribution-notice/" },
  { label: "Your Privacy Choices", path: "/privacy-choices/" },
];

export default function Footer() {
  return (
    <footer className="relative overflow-hidden bg-secondary text-secondary-foreground">
      {/* Subtle glass orbs in the background */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-20 -right-20 h-[300px] w-[300px] rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute bottom-0 -left-20 h-[200px] w-[200px] rounded-full bg-accent/8 blur-3xl" />
      </div>

      <div className="relative container py-16">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <img src={enzosLogo} alt="Enzo's Cleaning Solutions" className="h-14 w-auto mb-4" />
            <p className="text-secondary-foreground/70 text-sm leading-relaxed">Fleet Preservation Programs — sales, service, and installation of wash bay equipment, pressure washers, detergents and cleaning equipment that protect, preserve, and prolong the life of your fleet.</p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <h3 className="font-heading text-lg font-bold mb-4">Quick Links</h3>
            <ul className="grid grid-cols-2 gap-x-4 gap-y-2">
              {quickLinks.filter((l) => l.path !== "/articles/" || getHasArticles()).map((link) => (
                <li key={link.path}><Link to={link.path} className="text-sm text-secondary-foreground/70 hover:text-primary-foreground transition-colors">{link.label}</Link></li>
              ))}
            </ul>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h3 className="font-heading text-lg font-bold mb-4">Contact</h3>
            <ul className="space-y-3">
              <li><a href="tel:4195020007" className="flex items-start gap-3 text-sm text-secondary-foreground/70 hover:text-primary-foreground transition-colors"><Phone className="h-4 w-4 mt-0.5 flex-shrink-0" />419-502-0007</a></li>
              <li className="flex items-start gap-3 text-sm text-secondary-foreground/70"><MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />2003 Superior St., Sandusky, OH 44870</li>
              <li className="flex items-start gap-3 text-sm text-secondary-foreground/70"><Clock className="h-4 w-4 mt-0.5 flex-shrink-0" />Mon–Fri: 8am – 5pm</li>
            </ul>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <h3 className="font-heading text-lg font-bold mb-4">Follow Us</h3>
            <div className="flex gap-3">
              <a href="https://www.facebook.com/EnzosCleaning" target="_blank" rel="noopener noreferrer" className="rounded-full bg-secondary-foreground/10 p-3 hover:bg-primary hover:scale-110 transition-all duration-300" aria-label="Facebook">
                <svg className="h-5 w-5 fill-current" viewBox="0 0 24 24"><path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"/></svg>
              </a>
              <a href="https://www.youtube.com/channel/UCBwyiTH6Acs0ubiRf10S2Ng" target="_blank" rel="noopener noreferrer" className="rounded-full bg-secondary-foreground/10 p-3 hover:bg-primary hover:scale-110 transition-all duration-300" aria-label="YouTube">
                <svg className="h-5 w-5 fill-current" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
              </a>
            </div>
            <div className="mt-6">
              <h4 className="text-sm font-semibold mb-2">Our Partners</h4>
              <div className="flex flex-wrap gap-2">
                {["Hotsy", "Mi-T-M", "Kärcher", "Vital Oxide"].map((p) => (
                  <span key={p} className="text-xs bg-secondary-foreground/10 rounded-full px-3 py-1 backdrop-blur-sm">{p}</span>
                ))}
              </div>
              <img
                src="/uploads/partners/the-neutralizer.png"
                alt="The Neutralizer — drive-over undercarriage salt neutralization system by Enzo's Cleaning Solutions"
                className="mt-3 h-10 w-auto object-contain"
                loading="lazy"
              />
            </div>
          </motion.div>
        </div>
      </div>
      <div className="relative border-t border-secondary-foreground/10">
        {/* Extra bottom room below md so the floating stack (full-width
            assessment CTA plus the call button above it — FloatingActions)
            never sits on top of this line at the end of the page. */}
        <div className="container pt-6 pb-36 text-center text-sm text-secondary-foreground/50 md:pb-6">© {new Date().getFullYear()} Enzo's Cleaning Solutions LLC. All rights reserved. · {legalLinks.map((l, i) => (
          <span key={l.path}>{i > 0 && " · "}<Link to={l.path} className="hover:text-primary-foreground underline-offset-2 hover:underline">{l.label}</Link></span>
        ))}</div>
      </div>
    </footer>
  );
}
